'use client';
import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useClickOutside } from '@/hooks/useClickOutside';
import { cn } from '@/lib/utils';

import Button from '@/decodo/components/atoms/Button';
import Card from '@/decodo/components/atoms/Card';
import { Overlay, OverlayProps } from '@/decodo/components/atoms/Overlay';

import IconXClose from '~/icons/x-close.svg';

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  hasClose?: boolean;
  closeIsOutside?: boolean;
  closeOnOutsideClick?: boolean;
  fullSizeMobile?: boolean;
  classNames?: {
    root?: string;
    close?: string;
    closeCross?: string;
    modal?: string;
    content?: string;
    overlay?: string;
  };
  overlayProps?: OverlayProps;
  dataTrackerSection?: string;
  styles?: {
    root?: CSSProperties;
    modal?: CSSProperties;
  };
  lockScroll?: boolean;
  backgroundClickable?: boolean;
};

export function Modal({
  isOpen,
  children,
  hasClose = true,
  closeIsOutside = false,
  closeOnOutsideClick = true,
  backgroundClickable = false,
  onClose,
  classNames,
  fullSizeMobile,
  overlayProps = {},
  dataTrackerSection,
  styles,
  lockScroll = true,
}: ModalProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const lastScrollPosition = useRef<number>(0);

  const [mounted, setMounted] = useState<boolean>(false);

  useClickOutside({
    ref: ref as RefObject<HTMLDivElement>,
    callback: () => {
      if (!closeOnOutsideClick) return;

      if (!isOpen) return;
      onCloseModal();

      if (onClose) {
        onClose();
      }
    },
    excludeDataAttr: ['data-header-blur-background', 'data-modal'],
  });

  const onCloseModal = () => {
    if (!lockScroll) return;

    const body = document.body;
    const html = document.documentElement;

    body.classList.remove('remove-scroll');
    html.classList.add('!scroll-smooth');
    window.scrollTo({
      left: 0,
      behavior: 'instant',
      top: lastScrollPosition.current,
    });
  };

  const onOpenModal = () => {
    if (!lockScroll) return;

    const body = document.body;
    const html = document.documentElement;

    lastScrollPosition.current = window.scrollY;

    html.classList.remove('!scroll-smooth');
    body.style.setProperty('--top-scroll', `-${window.scrollY}px`);
    body.classList.add('remove-scroll');
  };

  useEffect(() => {
    if (isOpen) onOpenModal();
    else mounted && onCloseModal();
  }, [isOpen]);

  useEffect(() => setMounted(true), []);

  const CloseButton = () => (
    <Button
      onClick={onClose}
      classNames={{
        root: cn('absolute right-4 top-4 p-2', classNames?.close),
      }}
      slim
      variant='text'
      color='secondary'
      data-tracker-context='modal-close'
    >
      <IconXClose className={cn('h-4 w-4', classNames?.closeCross)} />
    </Button>
  );

  if (!mounted) return null;

  return (
    <>
      {createPortal(
        <div
          className={cn(isOpen ? 'block' : 'hidden')}
          data-tracker-section={dataTrackerSection}
        >
          <Overlay
            {...overlayProps}
            classNames={{ root: cn(classNames?.overlay) }}
          />
          <div
            className={cn(
              'fixed left-0 top-0 z-50 flex items-center justify-center',
              !backgroundClickable && 'h-full w-full',
              classNames?.root
            )}
            ref={ref}
            data-tracker-component='modal'
            data-modal
            style={styles?.root}
          >
            {hasClose && closeIsOutside && <CloseButton />}
            <Card
              data-tracker-component='modal'
              roudnessLevel='s'
              classNames={{
                root: cn(
                  'px-5 pb-8 pt-12 overflow-auto ',
                  fullSizeMobile
                    ? 'm-0 h-[calc(100%_-_4rem)] md:h-auto md:m-5'
                    : 'm-5',
                  classNames?.modal
                ),
              }}
              style={styles?.modal}
            >
              {hasClose && !closeIsOutside && <CloseButton />}
              <div
                className={cn(
                  'h-full w-full overflow-auto',
                  classNames?.content
                )}
              >
                {children}
              </div>
            </Card>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
